import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Checkbox, Field } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { useFormDraft } from '@/components/ui/useFormDraft'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { isDecimal, isWholeNumber, normaliseDecimal } from '@/lib/decimal'
import {
  isPackageTypeCode,
  PACKAGE_TYPE_OTHER,
  packageTypeOptions,
} from '@/lib/packageTypes'
import { fieldErrorKey } from '@/lib/problems'
import type { CargoPosition } from '@/types/domain'

/* The cargo position editor — where UJ-1 is won or lost.
 *
 * The PRD's counter-signal is the 50 CBM declaration that arrives as a 90 CBM
 * truck. So volume is REQUIRED here, and chemical cargo adds MSDS and the
 * transport certificate to the checklist (FR-12, FR-13). The sheet does not
 * merely collect these — it says what an omission blocks, at the moment of
 * entry, so the refusal to send later is never a surprise.
 */

export interface CargoDraft {
  description: string
  hsCode: string
  shipperName: string
  packages: string
  /** What is stored: one of the picker's codes, the text typed under
   *  «Другое…», or blank. */
  packageType: string
  /** The picker sits on «Другое…», so `packageType` is the person's own text.
   *  A form-only flag: the payload carries `packageType` alone, so the
   *  picker's marker can never reach the server. */
  packageTypeOther: boolean
  weightKg: string
  volumeCbm: string
  chargeableWeightKg: string
  lengthCm: string
  widthCm: string
  heightCm: string
  isChemical: boolean
  hasMsds: boolean
  hasTransportCertificate: boolean
}

/* Every numeric field reads through `lib/decimal`, the one normaliser: «22 000»
   (the cargo table's own output, U+202F and all), «22 000,5» and «22000.5» are
   one number. Accepting the shape here and sending it raw is what #134 (N-13)
   found — the server refused `"weightKg":"22 000"`. So the check and the draft
   that leaves the sheet use the same function, and cannot disagree. */
const NUMERIC_FIELDS = [
  'packages',
  'weightKg',
  'volumeCbm',
  'chargeableWeightKg',
  'lengthCm',
  'widthCm',
  'heightCm',
] as const satisfies readonly (keyof CargoDraft)[]

/** Blank is allowed where the field is optional; a non-numeric entry is not. */
function isBlankOrNumber(raw: string): boolean {
  return raw.trim() === '' || isDecimal(raw)
}

/** `Number()` only to compare with zero — never to build the value (AD-9). */
function isPositiveNumber(raw: string): boolean {
  return isDecimal(raw) && Number(normaliseDecimal(raw)) > 0
}

/** The package count is an integer on the wire: «2,5» would not decode. */
function isPackageCount(raw: string): boolean {
  return isWholeNumber(raw) && Number(normaliseDecimal(raw)) > 0
}

/** The draft as it leaves the sheet: every figure in the dotted, ungrouped
 *  shape the server parses. Strings still — the payload builder keeps them so. */
function normaliseFigures(draft: CargoDraft): CargoDraft {
  const next = { ...draft }
  for (const key of NUMERIC_FIELDS) next[key] = normaliseDecimal(draft[key])
  return next
}

type DimensionKey = 'lengthCm' | 'widthCm' | 'heightCm'

export function CargoFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  onDelete,
  pending,
  error,
  serverErrors,
}: {
  open: boolean
  onClose: () => void
  record?: CargoPosition
  onSubmit: (values: CargoDraft) => void
  /** Removes an existing position. Absent when adding — there is nothing to
   *  remove yet, and a delete affordance on a blank form is a trap. */
  onDelete?: () => void
  pending?: boolean
  /** A refusal from the server, shown above the fields with the draft intact. */
  error?: string
  /** The same refusal's field codes (`problem.errors`), keyed by the payload's
   *  field name — `weightKg`, `packages`. Each renders under the field it names,
   *  so a 422 says WHICH figure was refused rather than only that one was. */
  serverErrors?: Record<string, string[]>
}) {
  const { t } = useTranslation()
  const dimensionsHintId = useId()

  /* A half-pressed delete must not survive the sheet closing: reopening on
     another position with the confirmation already showing would put the
     dangerous button under a click meant for something else.

     Keyed on the record rather than reset in an effect — an effect that
     setStates on `open` costs a second render pass every time the sheet opens,
     and React's own guidance is to derive from a key instead. Changing the key
     remounts the confirmation in its closed state for free. */
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  /* The draft as it stood when this open last submitted. A server field code is
     about THAT value: it marks a field only while the field still holds what
     was sent, so retyping the figure clears the mark, and a refusal left over
     from an earlier open never accuses a draft that was not sent. Reset with
     the confirmation, on the same key, for the same reason. */
  const [submitted, setSubmitted] = useState<CargoDraft | null>(null)
  const [confirmKey, setConfirmKey] = useState<string | null>(null)
  const currentKey = open ? (record?.id ?? 'new') : null
  if (confirmKey !== currentKey) {
    setConfirmKey(currentKey)
    setConfirmingDelete(false)
    setSubmitted(null)
  }

  const [draft, setDraft, touched, markTouched] = useFormDraft<CargoDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            description: record.description,
            hsCode: record.hsCode ?? '',
            shipperName: record.shipperName ?? '',
            packages: String(record.packages),
            packageType: record.packageType,
            // A type typed before the picker existed — "Pallet" with a capital
            // included — opens as «Другое…» with its text, unchanged.
            packageTypeOther:
              record.packageType !== '' && !isPackageTypeCode(record.packageType),
            weightKg: record.weightKg ?? '',
            volumeCbm: record.volumeCbm ?? '',
            chargeableWeightKg: record.chargeableWeightKg ?? '',
            lengthCm: record.lengthCm ?? '',
            widthCm: record.widthCm ?? '',
            heightCm: record.heightCm ?? '',
            isChemical: record.isChemical,
            hasMsds: record.hasMsds ?? false,
            hasTransportCertificate: record.hasTransportCertificate ?? false,
          }
        : emptyCargo,
  )

  /* The server's code for a field, while the field still holds what was sent.
     The first code only: the server names one problem per field here. A local
     error outranks it at every call site — it is about the draft as it is now. */
  const serverError = (key: keyof CargoDraft): string | undefined => {
    if (!submitted || draft[key] !== submitted[key]) return undefined
    const code = serverErrors?.[key]?.[0]
    return code ? t(fieldErrorKey(code)) : undefined
  }

  const descriptionError =
    touched && !draft.description.trim()
      ? t('common.form.required')
      : serverError('description')
  const packagesError =
    touched && !isPackageCount(draft.packages)
      ? t('quotes.cargo.packagesInvalid')
      : serverError('packages')
  /* Volume is needed before the offer goes out, and NOT before the position is
     saved. The distinction is the whole design of FR-12: a form that refused
     the save would be answered by inventing a number, and the invented number
     is what reaches the client as the 50 CBM declaration behind a 90 CBM
     truck. So an absent volume is a WARNING here — it names what it will
     block — while the checklist reports it and the send refuses it.

     A non-numeric entry is still refused: that is a typo, not an omission. */
  const volumeMissing = draft.volumeCbm.trim() === ''
  const volumeError =
    touched && !volumeMissing && !isPositiveNumber(draft.volumeCbm)
      ? t('common.form.invalidNumber')
      : serverError('volumeCbm')
  const dimensionsMissing = [draft.lengthCm, draft.widthCm, draft.heightCm].some(
    (value) => value.trim() === '',
  )
  /* The optional measures — gross weight, chargeable weight (most road cargo
     has none of its own) and the three dimensions — are refused only when they
     are a typo, never for being blank. */
  const optionalError = (
    key: 'weightKg' | 'chargeableWeightKg' | DimensionKey,
  ): string | undefined =>
    touched && !isBlankOrNumber(draft[key])
      ? t('common.form.invalidNumber')
      : serverError(key)
  const weightError = optionalError('weightKg')
  const chargeableWeightError = optionalError('chargeableWeightKg')

  const invalid =
    !draft.description.trim() ||
    !isPackageCount(draft.packages) ||
    (!volumeMissing && !isPositiveNumber(draft.volumeCbm)) ||
    !isBlankOrNumber(draft.weightKg) ||
    !isBlankOrNumber(draft.chargeableWeightKg) ||
    !isBlankOrNumber(draft.lengthCm) ||
    !isBlankOrNumber(draft.widthCm) ||
    !isBlankOrNumber(draft.heightCm)

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('quotes.cargo.edit') : t('quotes.cargo.add')}
      pending={pending}
      // The server's refusal outranks the local one: it is about this save,
      // where the field errors are about the form in general.
      error={error ?? (touched && invalid ? t('common.form.fixFields') : undefined)}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        setSubmitted(draft)
        onSubmit(normaliseFigures(draft))
      }}
    >
      <Field label={t('quotes.cargo.position')} required error={descriptionError}>
        {(props) => (
          <Input
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('quotes.cargo.hsCode')}
        error={serverError('hsCode')}
        hint={t('common.form.optional')}
      >
        {(props) => (
          <Input
            value={draft.hsCode}
            onChange={(e) => setDraft({ ...draft, hsCode: e.target.value })}
            className="font-mono"
            {...props}
          />
        )}
      </Field>

      <div className="flex gap-2.5">
        <Field
          label={t('quotes.cargo.packages')}
          required
          error={packagesError}
          className="min-w-0 flex-1"
        >
          {(props) => (
            <Input
              inputMode="numeric"
              value={draft.packages}
              onChange={(e) => setDraft({ ...draft, packages: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>

        {/* A pick, not free text (F3): the code is stored and every reader
            words it in its own language, so a Russian offer no longer prints
            «Pallet». «Другое…» keeps the trade's open vocabulary — it reveals
            a text field whose words are printed as typed. */}
        <Field
          label={t('quotes.cargo.packageType')}
          // The code is about the stored value. Under «Другое…» that is the
          // typed text, so the text field below carries it instead.
          error={draft.packageTypeOther ? undefined : serverError('packageType')}
          className="min-w-0 flex-1"
        >
          {(props) => (
            <Select
              value={draft.packageTypeOther ? PACKAGE_TYPE_OTHER : draft.packageType}
              onChange={(choice) =>
                setDraft(
                  choice === PACKAGE_TYPE_OTHER
                    ? // Starts blank: the code just left is not the person's text.
                      {
                        ...draft,
                        packageTypeOther: true,
                        packageType: draft.packageTypeOther ? draft.packageType : '',
                      }
                    : { ...draft, packageTypeOther: false, packageType: choice },
                )
              }
              options={packageTypeOptions(t)}
              {...props}
            />
          )}
        </Field>
      </div>

      {draft.packageTypeOther ? (
        <Field label={t('quotes.packageTypes.otherText')} error={serverError('packageType')}>
          {(props) => (
            <Input
              value={draft.packageType}
              onChange={(e) => setDraft({ ...draft, packageType: e.target.value })}
              // The server's bound on a stored package type (quote/cargo.go).
              maxLength={64}
              {...props}
            />
          )}
        </Field>
      ) : null}

      {/* Who ships THIS position (FR-12). Blank on the ordinary quote, where
          the header names one shipper; it earns its place on the consolidated
          truck, where the offer has to say which boxes came from whom. */}
      <Field
        label={t('quotes.cargo.shipper')}
        error={serverError('shipperName')}
        hint={t('quotes.cargo.shipperHint')}
      >
        {(props) => (
          <Input
            value={draft.shipperName}
            onChange={(e) => setDraft({ ...draft, shipperName: e.target.value })}
            {...props}
          />
        )}
      </Field>

      <div className="flex gap-2.5">
        <Field label={t('quotes.cargo.weight')} error={weightError} className="min-w-0 flex-1">
          {(props) => (
            <Input
              inputMode="decimal"
              value={draft.weightKg}
              onChange={(e) => setDraft({ ...draft, weightKg: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>

        <Field
          label={t('quotes.cargo.volume')}
          error={volumeError}
          // Says what an omission will block, at the moment of entry, so the
          // send refusing later is never a surprise (FR-12).
          hint={
            volumeError
              ? undefined
              : volumeMissing
                ? t('quotes.cargo.volumeRequired')
                : t('quotes.cargo.volumeHint')
          }
          className="min-w-0 flex-1"
        >
          {(props) => (
            <Input
              inputMode="decimal"
              value={draft.volumeCbm}
              onChange={(e) => setDraft({ ...draft, volumeCbm: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>
      </div>

      {/* What the carrier bills when that is not the gross weight (FR-13).
          Entered rather than computed: the volumetric divisor belongs to the
          carrier, and asserting one of our own would invent the figure. */}
      <Field
        label={t('quotes.cargo.chargeableWeight')}
        error={chargeableWeightError}
        hint={chargeableWeightError ? undefined : t('quotes.cargo.chargeableWeightHint')}
      >
        {(props) => (
          <Input
            inputMode="decimal"
            value={draft.chargeableWeightKg}
            onChange={(e) =>
              setDraft({ ...draft, chargeableWeightKg: e.target.value })
            }
            className="asa-num"
            {...props}
          />
        )}
      </Field>

      {/* `min-w-0` on the fieldset: a fieldset's default min-width is its
          content's, so three inputs at their intrinsic width pushed «Высота»
          past the dialog's edge (N-14). */}
      <fieldset
        className="flex min-w-0 flex-col gap-1.5"
        aria-describedby={dimensionsMissing ? dimensionsHintId : undefined}
      >
        <legend className="text-text-body pb-1.5 text-[12.5px] leading-none">
          {t('quotes.cargo.dimensions')}
        </legend>
        <div className="flex gap-2.5">
          {(['lengthCm', 'widthCm', 'heightCm'] as const).map((key) => (
            <Field
              key={key}
              label={t(`quotes.cargo.${key}`)}
              // Each dimension's own error, under it; the group's «needed to
              // send» hint below stays, since it is about all three (N-15).
              error={optionalError(key)}
              className="min-w-0 flex-1"
            >
              {(props) => (
                <Input
                  inputMode="decimal"
                  value={draft[key]}
                  onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                  className="asa-num"
                  {...props}
                />
              )}
            </Field>
          ))}
        </div>
        {/* The volume's rule, for the same reason (FR-12, N-15): all three are
            needed to send — HasDimensions is all, not any — and none to save.
            Quiet once they are all there. */}
        {dimensionsMissing ? (
          <p
            id={dimensionsHintId}
            className="text-text-muted text-[12px] leading-snug font-light"
          >
            {t('quotes.cargo.dimensionsRequired')}
          </p>
        ) : null}
      </fieldset>

      {/* Chemical cargo adds MSDS and the transport certificate to the
          checklist (FR-13). Ticking the box is what reveals them, so the two
          extra requirements appear at the moment they start applying. */}
      <Checkbox
        label={t('quotes.cargo.isChemical')}
        checked={draft.isChemical}
        onCheckedChange={(checked) => setDraft({ ...draft, isChemical: checked })}
      />

      {draft.isChemical ? (
        <div className="border-attention/35 bg-attention-soft/40 flex flex-col gap-2 rounded-md border px-3 py-2.5">
          <p className="text-text-body text-[12px] leading-snug font-light">
            {t('quotes.cargo.chemicalNote')}
          </p>
          <Checkbox
            label={t('quotes.cargo.hasMsds')}
            checked={draft.hasMsds}
            onCheckedChange={(checked) => setDraft({ ...draft, hasMsds: checked })}
          />
          <Checkbox
            label={t('quotes.cargo.hasTransportCertificate')}
            checked={draft.hasTransportCertificate}
            onCheckedChange={(checked) =>
              setDraft({ ...draft, hasTransportCertificate: checked })
            }
          />
        </div>
      ) : null}

      {/* Removing sits at the FOOT of the form, apart from the footer's Save,
          rather than beside it: FormSheet keeps the dangerous action off the
          same muscle memory as the safe one, and a delete button next to Save
          is exactly that collision. Two presses, because the first is easy to
          hit by accident and a removed position cannot be recovered. */}
      {onDelete ? (
        <div className="border-line-hair mt-1 border-t pt-3">
          {confirmingDelete ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-text-body flex-1 text-[12.5px] leading-snug">
                {t('quotes.cargo.deleteConfirm')}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmingDelete(false)}
                disabled={pending}
              >
                {t('common.actions.cancel')}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={onDelete}
                disabled={pending}
              >
                {t('quotes.cargo.deleteConfirmAction')}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              disabled={pending}
              className="text-negative cursor-pointer text-[12.5px] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('quotes.cargo.delete')}
            </button>
          )}
        </div>
      ) : null}
    </FormSheet>
  )
}

const emptyCargo: CargoDraft = {
  description: '',
  hsCode: '',
  shipperName: '',
  packages: '',
  packageType: '',
  packageTypeOther: false,
  weightKg: '',
  volumeCbm: '',
  chargeableWeightKg: '',
  lengthCm: '',
  widthCm: '',
  heightCm: '',
  isChemical: false,
  hasMsds: false,
  hasTransportCertificate: false,
}
